import { useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { QuartersApplicationStatus } from "../types/status";
import type {
  BuildingForm,
  FloorForm,
  FlatForm,
  ReservationForm,
  IWeightConfig,
  IEmployeeDetails,
  IDocumentItem,
  FlatFilterParams,
  ApplicationFilterParams,
  AllotmentFilterParams,
  AuditLogFilterParams,
  IProvisionalAllotment,
  IProvisionalWaitlistItem,
} from "../modules/quarters/types/quarters.types";

interface QuartersServiceParams {
  apiRequest: (url: string, options?: RequestInit) => Promise<any>;
}

/** Serialise filter param objects for query strings, omitting undefined/empty values. */
const toQuery = (params: Record<string, string | number | undefined>): string => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== "") sp.set(k, String(v));
  });
  return sp.toString();
};

export const createQuartersService = ({ apiRequest }: QuartersServiceParams) => ({
  // ── Buildings ──────────────────────────────────────────────────────────────

  getBuildings: async (params: { quarterType?: string } = {}) => {
    const query = toQuery(params);
    return await apiRequest(`/quarters/buildings${query ? `?${query}` : ""}`);
  },
  /**
   * Returns { success, data: { building, floors, stats } }
   * NOTE: floors[] are nested inside data, not data itself.
   */
  getBuilding: async (id: string) => {
    return await apiRequest(`/quarters/buildings/${id}`);
  },
  createBuilding: async (data: BuildingForm) => {
    return await apiRequest("/quarters/buildings", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  updateBuilding: async (id: string, data: Partial<BuildingForm>) => {
    return await apiRequest(`/quarters/buildings/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  deleteBuilding: async (id: string) => {
    return await apiRequest(`/quarters/buildings/${id}`, {
      method: "DELETE",
    });
  },

  // ── Floors ─────────────────────────────────────────────────────────────────

  /**
   * quarterType is intentionally absent from FloorForm — the backend strips it
   * from this request; the building's quarterType applies to all its flats.
   */
  createFloor: async (buildingId: string, data: FloorForm) => {
    return await apiRequest(`/quarters/buildings/${buildingId}/floors`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  updateFloor: async (id: string, data: Partial<FloorForm> & { sequence?: number }) => {
    return await apiRequest(`/quarters/floors/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  deleteFloor: async (id: string) => {
    return await apiRequest(`/quarters/floors/${id}`, {
      method: "DELETE",
    });
  },
  getFloorFlats: async (floorId: string) => {
    return await apiRequest(`/quarters/floors/${floorId}/flats`);
  },

  // ── Flats ──────────────────────────────────────────────────────────────────

  /**
   * quarterType filter is resolved through Building on the backend.
   * Flat documents do NOT store quarterType — it comes from their building.
   */
  getFlats: async (params: FlatFilterParams = {}) => {
    const query = toQuery(params as Record<string, string | number | undefined>);
    return await apiRequest(`/quarters/flats${query ? `?${query}` : ""}`);
  },
  /** Returns { success, data: { flat, history } } */
  getFlat: async (id: string) => {
    return await apiRequest(`/quarters/flats/${id}`);
  },
  /** quarterType is stripped server-side even if sent — do not include in FlatForm. */
  createFlat: async (data: Omit<FlatForm, "area" | "reservedFor"> & { area?: number; reservedFor?: string | null }) => {
    return await apiRequest("/quarters/flats", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  updateFlat: async (
    id: string,
    data: Partial<Omit<FlatForm, "area" | "reservedFor">> & { area?: number; reservedFor?: string | null }
  ) => {
    return await apiRequest(`/quarters/flats/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  deleteFlat: async (id: string) => {
    return await apiRequest(`/quarters/flats/${id}`, {
      method: "DELETE",
    });
  },

  // ── Reservation Categories ─────────────────────────────────────────────────

  getReservations: async () => {
    return await apiRequest("/quarters/reservations");
  },
  createReservation: async (data: ReservationForm) => {
    return await apiRequest("/quarters/reservations", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  updateReservation: async (id: string, data: Partial<ReservationForm>) => {
    return await apiRequest(`/quarters/reservations/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  deleteReservation: async (id: string) => {
    return await apiRequest(`/quarters/reservations/${id}`, {
      method: "DELETE",
    });
  },

  // ── Weight Config ──────────────────────────────────────────────────────────
  getParameters: async () => {
    const response = await apiRequest("/quarters/weights/parameters", { method: "GET" });
    return response;
  },
  getWeights: async () => {
    return await apiRequest("/quarters/weights");
  },
  updateWeights: async (configs: IWeightConfig[]) => {
    return await apiRequest("/quarters/weights", {
      method: "PUT",
      body: JSON.stringify({ configs }),
    });
  },

  // ── Applications ───────────────────────────────────────────────────────────

  getApplications: async (params: ApplicationFilterParams = {}) => {
    const query = toQuery(params as Record<string, string | number | undefined>);
    return await apiRequest(`/quarters/applications${query ? `?${query}` : ""}`);
  },
  getMyApplications: async () => {
    return await apiRequest("/quarters/applications/my-applications");
  },
  getApplication: async (id: string) => {
    return await apiRequest(`/quarters/applications/${id}`);
  },
  createApplication: async (data: {
    quarterType: string;
    employeeDetails: IEmployeeDetails;
    documents: IDocumentItem[];
    status: QuartersApplicationStatus | string;
  }) => {
    return await apiRequest("/quarters/applications", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  updateApplication: async (id: string, data: {
    quarterType?: string;
    employeeDetails?: IEmployeeDetails;
    documents?: IDocumentItem[];
    status?: QuartersApplicationStatus | string;
  }) => {
    return await apiRequest(`/quarters/applications/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },
  verifyApplication: async (id: string, data: {
    status: QuartersApplicationStatus | string;
    verificationNotes?: string;
    remarks?: string;
  }) => {
    return await apiRequest(`/quarters/applications/${id}/verify`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  // ── Allotment & Engines ────────────────────────────────────────────────────

  runRankingEngine: async (tieBreaker: string) => {
    return await apiRequest("/quarters/allotment/rank", {
      method: "POST",
      body: JSON.stringify({ tieBreaker }),
    });
  },
  runAutoAllotment: async (quarterType?: string) => {
    return await apiRequest("/quarters/allotment/auto", {
      method: "POST",
      body: JSON.stringify({ quarterType }),
    });
  },
  confirmAutoAllotments: async (
    allotments: IProvisionalAllotment[],
    waitlist?: IProvisionalWaitlistItem[]
  ) => {
    return await apiRequest("/quarters/allotment/auto/confirm", {
      method: "POST",
      body: JSON.stringify({ allotments, waitlist }),
    });
  },
  runManualAllotment: async (data: {
    applicationId?: string;
    flatId: string;
    userId: string;
    remarks?: string;
  }) => {
    return await apiRequest("/quarters/allotment/manual", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
  executeAllotmentAction: async (
    allotmentId: string,
    action: string,
    remarks?: string,
    date?: string
  ) => {
    return await apiRequest(`/quarters/allotment/${allotmentId}/action`, {
      method: "POST",
      body: JSON.stringify({ action, remarks, date }),
    });
  },
  getAllotments: async (params: AllotmentFilterParams = {}) => {
    const query = toQuery(params as Record<string, string | number | undefined>);
    return await apiRequest(`/quarters/allotments${query ? `?${query}` : ""}`);
  },
  getMyQuarters: async () => {
    return await apiRequest("/quarters/my-quarters");
  },

  // ── Dashboard Stats ────────────────────────────────────────────────────────

  getDashboardStats: async () => {
    return await apiRequest("/quarters/dashboard-stats");
  },

  // ── Audit Logs ─────────────────────────────────────────────────────────────

  getAuditLogs: async (params: AuditLogFilterParams = {}) => {
    const query = toQuery(params as Record<string, string | number | undefined>);
    return await apiRequest(`/quarters/audit-logs${query ? `?${query}` : ""}`);
  },

  // ── Notifications ──────────────────────────────────────────────────────────

  // TODO
  getMyNotifications: async () => {
    return {
      success: true,
      data: []
    }
  },
  // TODO
  markNotificationRead: async (id: string) => {
    return {
      success: true,
      data: {}
    }
  },
});

export const useQuartersService = () => {
  const { apiRequest } = useAuth();
  return useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
};

